import { useContext, useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { ArrowRightOutlined, CalendarOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons'

import { CardContractProgram } from './ui'
import { FieldsErrorType, ICardContractProgram, ICardContractProgramContainer, IFieldsError } from './types'
import css from './ui.module.scss'

import { useDict } from '@api/dict/state'
import { useCommon } from '@api/common/state'
import { useContracts } from '@api/contracts/state'
import { ICourseProgramsList } from '@api/dict/state.types'

import { useValue } from '@hooks/useValue'
import { validFormData } from '@hooks/useForm'
import { useDebounce } from '@hooks/useDebounce'
import { useFieldsErrorAnimation } from '@hooks/useFieldsErrorAnimation'

import { DictNameType } from '@smart/dictSelector'
import CommonService from '@services/common.service'

import { NotificationContext, notifyOpen } from '@ui/notification/notification.provider'
import { IModalConfirm, ModalConfirm } from '@primitive/modal.confirm'



/** Добавление или редактирование программы по договору - Контейнер */
export const CardContractProgramContainer = ({isOpen, cardMode, contractID, contractProgramID, dbName, handlerButtonOk, handlerButtonCancel}: ICardContractProgramContainer) => {
    const notify = useContext(NotificationContext)
    const loadingTitle = useValue<string>('Загрузка данных...')
    const modalConfirm = useValue<IModalConfirm>({isOpen: false})

    const {actionGetCoursePrograms} = useDict(useShallow((state) => state))
    const {actionGetEduDateStartEnd} = useCommon(useShallow((state) => state))

    const {isLoadingCard, getProgramData, saveCard} = useContracts(useShallow((state) => ({
        isLoadingCard: state.isLoadingCard,
        getProgramData: state.actionGetContractCardPrograms,
        saveCard: state.actionSetContractCardProgramsSave,
    })))

    const fieldsError = useValue<IFieldsError>({})
    const courseProgramList = useValue<ICourseProgramsList[]>([])
    
    const txtCourseProgramID = useValue<number | ''>('')

    const txtCourseNameID = useValue<number | ''>('')
    const txtCourseName = useValue<string>('')
    const txtProgramTypeID = useValue<number | ''>('')
    const txtProgramType = useValue<string>('')

    const txtDurationEducation = useValue<number | string>('')
    const txtVolumeHours = useValue<number | ''>('')
    const txtSumm = useValue<number | ''>('')

    const txtEducationDateStart = useValue<string>('')
    const txtEducationDateEnd = useValue<string>('')

    const txtSphere = useValue<string>('')
    const txtQualification = useValue<string>('')
    const txtRequirementName = useValue<string>('')


    useEffect(() => {
        if (isOpen) {
            getProgramData(Number(cardMode === 'new' ? contractID : contractProgramID), cardMode === 'new' ? 'new' : 'edit').then((res) => {
                if (res) {
                    txtCourseProgramID.setValue(res.courseProgramID)

                    txtCourseNameID.setValue(res.courseID || '')
                    txtCourseName.setValue(res.courseName || '')

                    txtProgramTypeID.setValue(res.documentTypeID || '')
                    txtProgramType.setValue(res.documentTypeName || '')
                
                    txtDurationEducation.setValue(res.durationEducation || '')
                    txtVolumeHours.setValue(res.volumeHours || '')
                    txtSumm.setValue(res.price || '')
                
                    txtEducationDateStart.setValue(res.startEducationDate || '')
                    txtEducationDateEnd.setValue(res.endEducationDate || '')
                
                    txtSphere.setValue(res.sphere || '')
                    txtQualification.setValue(res.qualification || '')
                    txtRequirementName.setValue(res.requirementName || '')
                }
            })
        }

        !isOpen && clearData()
    }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isOpen && txtCourseNameID.value) {
            actionGetCoursePrograms(dbName || '', txtCourseNameID.value || 0).then((res) => {
                courseProgramList.setValue(res)
            })
        }
    }, [isOpen, txtCourseNameID.value]) // eslint-disable-line react-hooks/exhaustive-deps


    /** Очистка формы */
    const clearData = () => {
        modalConfirm.setValue({isOpen: false})
        courseProgramList.setValue([])

        txtCourseProgramID.setValue('')
        
        txtCourseNameID.setValue('')
        txtCourseName.setValue('')
        txtProgramTypeID.setValue('')
        txtProgramType.setValue('')
    
        txtDurationEducation.setValue('')
        txtVolumeHours.setValue('')
        txtSumm.setValue('')
    
        txtEducationDateStart.setValue('')
        txtEducationDateEnd.setValue('')
    
        txtSphere.setValue('')
        txtQualification.setValue('')
        txtRequirementName.setValue('')
    }

    /** Получение дат, начала обучения и окончание обучения */
    const getEducationDate = (eduDateStart: string) => {
        actionGetEduDateStartEnd(eduDateStart, Number(txtDurationEducation.value), txtProgramTypeID.value === 2 ? 1 : 0).then((res) => {
            // res.eduStartDate && txtEducationDateStart.setValue(res.eduStartDate)
            res.eduEndDate && txtEducationDateEnd.setValue(res.eduEndDate)
        })
    }
    const getEducationDateEnd = (durationEducation: number) => {
        actionGetEduDateStartEnd(txtEducationDateStart.value, durationEducation, txtProgramTypeID.value === 2 ? 1 : 0).then((res) => {
            // res.eduStartDate && txtEducationDateStart.setValue(res.eduStartDate)
            res.eduEndDate && txtEducationDateEnd.setValue(res.eduEndDate)
        })
    }
    

    /** При выборе данных из справочника */
    const dictSelectorData = (dictName: DictNameType, data: {id: number, name: string, param1?: string, param2?: string, param3?: string, param4?: string, param5?: string, param6?: string, param7?: string, param8?: string, param9?: string}) => {
        switch (dictName) {
            case 'coursesContract': {
                txtCourseNameID.setValue(Number(data.id))
                txtCourseName.setValue(data.name)

                txtSphere.setValue(data.param1 || '')
                txtQualification.setValue(data.param2 || '')
                txtRequirementName.setValue(data.param3 || '')

                handleClickProgramList(Number(data.param7), Number(data.param4), Number(data.param5), Number(data.param6), Number(data.param8), data.param9 || '')
                break
            }

            case 'contractProgramInfo': {
                handleClickProgramList(Number(data.id), Number(data.param2), Number(data.param3), Number(data.param4), Number(data.param1), data.name)
                break
            }
        }
    }    

    /** Обработка клика по справочной информации, с последующей перезаписи продолжительности, обьема и суммы */
    const handleClickProgramList = (courseProgramID: number, durationEducation: number, volumeHours: number, price: number, programID: number, programName: string) => {
        txtCourseProgramID.setValue(courseProgramID)
        txtProgramTypeID.setValue(programID)
        txtProgramType.setValue(programName)

        txtDurationEducation.setValue(durationEducation)
        txtVolumeHours.setValue(volumeHours)
        txtSumm.setValue(price)

        actionGetEduDateStartEnd(txtEducationDateStart.value, Number(durationEducation), programID === 2 ? 1 : 0).then((res) => {
            res.eduEndDate && txtEducationDateEnd.setValue(res.eduEndDate)
        })
    }

    const debounceEduDate = useDebounce(getEducationDate, 1500) // Запуск получения дат начала обучения и окончания обучения
    const debounceEduDateEnd = useDebounce(getEducationDateEnd, 1500) // Запуск получения дат начала обучения и окончания обучения
    const txtEducationDateStartOnChange = (value: string) => {
        console.log(value)
        txtEducationDateStart.setValue(value)
        debounceEduDate(value)
    } // Поле дата начала обучения
    const txtDurationEducationOnChange = (value: string) => {
        txtDurationEducation.setValue(value.replace(',', '.'))

        const validateData = validFormData([{fieldName: 'durationEducation', value: value.replace(',', '.'), validType: 'numberDecimal', isRequired: true, errorMessage: ''}])
        if (validateData.length === 0) debounceEduDateEnd(value.replace(',', '.'))
    } // Поле продолжительность


    const saveProgram = async () => {
        loadingTitle.setValue(cardMode === 'new' ? 'Добавление программы...' : 'Сохранение данных...')

        try {
            const status = await saveCard(contractID || 0, contractProgramID || 0, {
                actionForm: cardMode || 'new',

                courseProgramID: Number(txtCourseProgramID.value),
                courseID: Number(txtCourseNameID.value),
                courseName: txtCourseName.value,
                documentTypeID: Number(txtProgramTypeID.value),
                durationEducation: txtDurationEducation.value.toString(),
                volumeHours: Number(txtVolumeHours.value),
                price: Number(txtSumm.value),
                sphere: txtSphere.value,
                qualification: txtQualification.value,
                requirementName: txtRequirementName.value,
                dateEducationStart: txtEducationDateStart.value,
                dateEducationEnd: txtEducationDateEnd.value
            })

            if (status === 200) {
                notify && notifyOpen(cardMode === 'new' ? 'Программа успешно добавлена.' : 'Изменения программы успешно сохранены.', 'success', 1500, notify)
                modalConfirm.setValue({isOpen: false})
                handlerButtonOk && handlerButtonOk()
            }

            return status
        } finally {
            loadingTitle.setValue('Загрузка данных...')
        }
    }

    const openEducationPeriodWarning = (expectedStartDate: string, expectedEndDate: string) => {
        const enteredPeriod = `${txtEducationDateStart.value} - ${txtEducationDateEnd.value}`
        const expectedPeriod = expectedStartDate && expectedEndDate
            ? `${expectedStartDate} - ${expectedEndDate}`
            : ''
        const duration = CommonService.formatDuration(Number(txtDurationEducation.value), txtProgramTypeID.value === 2)
        const contentText = expectedPeriod
            ? `Указанный период обучения (${enteredPeriod}) не соответствует продолжительности ${duration}. Ожидаемый период: ${expectedPeriod}. Сохранить указанные даты?`
            : `Не удалось автоматически проверить период обучения (${enteredPeriod}). Сохранить указанные даты без проверки?`

        modalConfirm.setValue({
            isOpen: true,
            maxWidth: '560px',
            containerClassName: css.educationPeriodWarningModal,
            headerText: 'Проверка срока обучения',
            contentText,
            content: (
                <div className={css.educationPeriodWarning}>
                    <div className={css.educationPeriodWarningSummary}>
                        <span className={css.educationPeriodWarningIcon} aria-hidden="true">
                            <WarningOutlined />
                        </span>
                        <div>
                            <strong>{expectedPeriod ? 'Период не совпадает с продолжительностью' : 'Автоматическая проверка недоступна'}</strong>
                            <p>
                                {expectedPeriod
                                    ? <>Для программы указана продолжительность <span className={css.educationPeriodWarningDuration}>{duration}</span>. Система рассчитала другую дату окончания.</>
                                    : 'Сверьте начало и окончание обучения вручную перед сохранением.'}
                            </p>
                        </div>
                    </div>

                    <div className={css.educationPeriodWarningPeriods}>
                        <div className={`${css.educationPeriodWarningPeriod} ${css.educationPeriodWarningEntered}`}>
                            <span>Указано</span>
                            <strong><CalendarOutlined aria-hidden="true" />{enteredPeriod}</strong>
                        </div>

                        {expectedPeriod && <>
                            <ArrowRightOutlined className={css.educationPeriodWarningArrow} aria-hidden="true" />
                            <div className={`${css.educationPeriodWarningPeriod} ${css.educationPeriodWarningExpected}`}>
                                <span>Рекомендуется</span>
                                <strong><CheckCircleOutlined aria-hidden="true" />{expectedPeriod}</strong>
                            </div>
                        </>}
                    </div>

                    <p className={css.educationPeriodWarningQuestion}>
                        {expectedPeriod
                            ? 'Сохранить указанный период без изменений?'
                            : 'Сохранить указанные даты без проверки?'}
                    </p>
                </div>
            ),
            cancelButtonText: 'Вернуться к датам',
            okButtonText: 'Сохранить как указано',
            handleButtonCancel() {modalConfirm.setValue({isOpen: false})},
            async handleButtonOk() {
                modalConfirm.setValue((currentValue) => ({...currentValue, isLoading: true}))

                try {
                    const status = await saveProgram()
                    if (status !== 200) modalConfirm.setValue((currentValue) => ({...currentValue, isLoading: false}))
                } catch {
                    modalConfirm.setValue((currentValue) => ({...currentValue, isLoading: false}))
                }
            },
        })
    }

    /** Кнопка сохранить все данные */
    const handleBtnSave = async () => {
        const validateData = validFormData([ // Валидируем данные 
            {fieldName: 'courseName', value: txtCourseNameID.value.toString(), validType: 'notEmpty', isRequired: true, errorMessage: 'Наименование курса не выбрано.'},
            {fieldName: 'programType', value: txtProgramTypeID.value.toString(), validType: 'notEmpty', isRequired: true, errorMessage: 'Тип программы не выбран.'},
            
            {fieldName: 'durationEducation', value: txtDurationEducation.value.toString(), validType: 'numberDecimal', isRequired: true, errorMessage: 'Продолжительность не корректное значение.'},
            {fieldName: 'volumeHours', value: txtVolumeHours.value.toString(), validType: 'number_1-9', isRequired: true, errorMessage: 'Объём не корректное значение.'},
            {fieldName: 'summ', value: txtSumm.value.toString(), validType: 'number_1-9', isRequired: true, errorMessage: 'Сумма не корректное значение.'},
            
            {fieldName: 'educationDateStart', value: txtEducationDateStart.value, validType: 'date', isRequired: true, errorMessage: 'Начало обучения, не корректная дата.'},
            {fieldName: 'educationDateEnd', value: txtEducationDateEnd.value, validType: 'date', isRequired: true, errorMessage: 'Конец обучения, не корректная дата.'},
            
            {fieldName: 'sphere', value: txtSphere.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Сфера не указано.'},
            {fieldName: 'qualification', value: txtQualification.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Квалификация не указано.'},
        ])

        for (const item of validateData) { // Выводим ошибки валидации
            fieldsErrorAnimation(item.fieldName as FieldsErrorType)
            notify && notifyOpen(item.message, 'warning', 2000, notify)
        }

        if (validateData.length > 0) return

        let expectedPeriod = {eduStartDate: '', eduEndDate: ''}
        try {
            expectedPeriod = await actionGetEduDateStartEnd(
                txtEducationDateStart.value,
                Number(txtDurationEducation.value),
                txtProgramTypeID.value === 2 ? 1 : 0,
            )
        } catch {
            openEducationPeriodWarning('', '')
            return
        }
        const hasEducationPeriodMismatch = !expectedPeriod.eduStartDate
            || !expectedPeriod.eduEndDate
            || expectedPeriod.eduStartDate !== txtEducationDateStart.value
            || expectedPeriod.eduEndDate !== txtEducationDateEnd.value

        if (hasEducationPeriodMismatch) {
            openEducationPeriodWarning(expectedPeriod.eduStartDate, expectedPeriod.eduEndDate)
            return
        }

        await saveProgram()
    }
    /** Активация анимации ошибки в указанных полях */
    const fieldsErrorAnimation = useFieldsErrorAnimation<IFieldsError>(fieldsError.setValue)


    /** Свойства передаваемые в компоненту */
    const propsToComponent: ICardContractProgram = {
        loadingTitle, 
        dbName: dbName || '',
        isOpen, cardMode, 
        
        isLoadingCard, fieldsError,
        txtCourseNameID, txtCourseName, txtProgramTypeID, txtProgramType,
        txtDurationEducation, txtVolumeHours, txtSumm,
        txtEducationDateStart, txtEducationDateEnd,
        txtSphere, txtQualification, txtRequirementName,

        dictSelectorData, txtEducationDateStartOnChange, txtDurationEducationOnChange, 
        handleBtnSave, handlerButtonCancel,
    }
    
    return <>   
        <CardContractProgram {...propsToComponent} />
        <ModalConfirm {...modalConfirm.value} />
    </>
}